import React, { useCallback } from 'react'

import { useDispatch } from 'react-redux'

import type { CollectionSlug, CollectionUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { actionTypes } from 'actions/actionTypes'
import { clickMerchandisingBanner } from 'actions/trackingKeys'
import { useSelectCollection } from 'hooks/menu-collections'
import type { SelectCollectionEvent } from 'routes/Menu/MenuExperience/MenuPage/types'
import type { WaveLinkHeaderProps } from 'routes/Menu/MenuRecipesPage/CollectionHeader/WaveLinkHeader/WaveLinkHeader'
import type { MenuHeadersIncludesData } from 'routes/Menu/domains/brand/types'

import { WaveLinkHeader } from '../WaveLinkHeader'

type OwnProps = {
  link: MenuHeadersIncludesData['attributes']['link']
}

const useChangeCollectionById = (collectionId: CollectionUUID) => {
  const selectCollection = useSelectCollection()
  const dispatch = useDispatch()

  return useCallback(() => {
    if (collectionId) {
      selectCollection(collectionId, (oldCollection, newCollection) => {
        if (newCollection && oldCollection?.id !== newCollection?.id) {
          // this action doesn't have a reducer, but tracking data is passed to snowplow v1 tracker
          dispatch({
            type: actionTypes.FILTERS_COLLECTION_CHANGE,
            collectionName: newCollection.slug,
            collectionId: newCollection.id,
            trackingData: {
              actionType: actionTypes.RECIPE_COLLECTION_SELECTED,
              collectionId: newCollection.id,
            },
          })

          dispatch({
            type: actionTypes.TRACKING,
            trackingData: {
              actionType: clickMerchandisingBanner,
              collection_id: newCollection.id,
              click_collection_id: oldCollection?.id || null,
            },
          })
        }
      })
    }
  }, [collectionId, dispatch, selectCollection])
}

const WaveLinkHeaderContainer = (props: Omit<OwnProps & WaveLinkHeaderProps, 'onClick'>) => {
  const { link } = props
  const collectionId = (link?.collectionId || '') as CollectionUUID
  const collectionSlug = (link?.collectionSlug || '') as CollectionSlug

  const changeCollectionById = useChangeCollectionById(collectionId)

  const onClick = () => {
    queueTrackingEvent<SelectCollectionEvent>({
      action: 'select_collection',
      properties: {
        collection_id: collectionId,
        collection_slug: collectionSlug,
        navigation_method: 'menu_banner',
      },
    })
    changeCollectionById()
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <WaveLinkHeader {...props} onClick={onClick} />
}

export { WaveLinkHeaderContainer }
